import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../services/api';
import { Article } from '../types';

const Timeline: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('Ancient');
  const timelineContainerRef = React.useRef<HTMLDivElement>(null);

  const periods = [
    { name: 'Ancient', startYear: -3500, endYear: 476 },
    { name: 'Medieval', startYear: 476, endYear: 1453 },
    { name: 'Renaissance', startYear: 1453, endYear: 1600 },
    { name: 'Exploration', startYear: 1400, endYear: 1700 },
    { name: 'Modern', startYear: 1900, endYear: 2024 }
  ];

  const scrollToPeriod = (periodName: string) => {
    setSelectedPeriod(periodName);
    const period = periods.find(p => p.name === periodName);
    if (period && timelineContainerRef.current) {
      // Find the first article in this period based on year
      const firstArticleInPeriod = articles.find(article => {
        const articleYear = article.year || 0;
        return articleYear >= period.startYear && articleYear <= period.endYear;
      });
      if (firstArticleInPeriod) {
        const eventElement = document.getElementById(`event-${firstArticleInPeriod.id}`);
        if (eventElement) {
          const containerRect = timelineContainerRef.current.getBoundingClientRect();
          const eventRect = eventElement.getBoundingClientRect();
          const scrollTop = timelineContainerRef.current.scrollTop + eventRect.top - containerRect.top - (containerRect.height / 2) + (eventRect.height / 2);
          
          timelineContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const articlesData = await ApiService.getArticles();
        // Sort articles by year (oldest first for timeline)
        const sortedArticles = Array.isArray(articlesData) 
          ? articlesData.filter(article => article.year).sort((a, b) => (a.year || 0) - (b.year || 0))
          : [];
        setArticles(sortedArticles);
      } catch (error) {
        console.error('Error fetching articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const formatYear = (year: number) => {
    if (year < 0) {
      return `${Math.abs(year)} BC`;
    }
    return `${year} AD`;
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  return (
    <div style={{ 
      backgroundColor: '#fdfcfb', 
      color: '#1b140e', 
      minHeight: '100vh' 
    }}>
      <main style={{ 
        flex: 1, 
        padding: '3rem 2.5rem' 
      }}>
        <div style={{ 
          margin: '0 auto', 
          maxWidth: '80rem' 
        }}>
          {/* Header */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '3rem' 
          }}>
            <h1 style={{
              fontSize: '3rem',
              fontWeight: '800',
              letterSpacing: '-0.025em',
              color: '#1b140e',
              fontFamily: 'Merriweather, serif',
              margin: '0 0 1rem 0'
            }}>
              Timeline of History
            </h1>
            <p style={{
              marginTop: '1rem',
              fontSize: '1.125rem',
              color: '#6b5a4b',
              margin: '0'
            }}>
              An interactive journey through the annals of time.
            </p>
          </div>

          {/* Quick Timeline Navigation */}
          <div style={{ 
            position: 'sticky',
            top: '0',
            zIndex: 10,
            backgroundColor: '#fdfcfb',
            marginBottom: '4rem', 
            padding: '2rem 0',
            borderBottom: '1px solid #e6dbd1'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              textAlign: 'center',
              color: '#4a3f35',
              marginBottom: '2rem',
              fontFamily: 'Merriweather, serif'
            }}>
              Quick Timeline
            </h2>
            <div style={{ 
              position: 'relative', 
              padding: '0 1rem' 
            }}>
              {/* Horizontal timeline line */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '0',
                width: '100%',
                height: '2px',
                backgroundColor: '#e6dbd1',
                transform: 'translateY(-50%)'
              }}></div>
              <div style={{ 
                position: 'relative', 
                display: 'flex', 
                justifyContent: 'space-between' 
              }}>
                {periods.map((period) => (
                  <button
                    key={period.name}
                    style={{
                      background: 'none',
                      border: 'none',
                      textAlign: 'center',
                      cursor: 'pointer',
                      padding: '0.5rem'
                    }}
                    onClick={() => scrollToPeriod(period.name)}
                  >
                    <div style={{ 
                      position: 'relative', 
                      display: 'inline-block' 
                    }}>
                      <div style={{
                        width: '1rem',
                        height: '1rem',
                        borderRadius: '50%',
                        backgroundColor: selectedPeriod === period.name ? '#c9731d' : '#e6dbd1',
                        transition: 'all 0.3s ease',
                        transform: selectedPeriod === period.name ? 'scale(1.5)' : 'scale(1)',
                        boxShadow: selectedPeriod === period.name ? '0 0 0 4px rgba(201, 115, 29, 0.2)' : 'none'
                      }}></div>
                    </div>
                    <p style={{
                      marginTop: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: selectedPeriod === period.name ? 'bold' : '500',
                      color: selectedPeriod === period.name ? '#c9731d' : '#6b5a4b',
                      transition: 'all 0.3s ease',
                      margin: '0.5rem 0 0 0'
                    }}>
                      {period.name}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Timeline */}
          <div style={{ 
            position: 'relative',
            height: 'calc(100vh - 300px)',
            overflow: 'hidden',
            border: '1px solid #e6dbd1',
            borderRadius: '0.5rem',
            backgroundColor: 'white'
          }}>
            <div 
              ref={timelineContainerRef}
              style={{
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative',
                padding: '2rem'
              }}
            >
              {/* Vertical timeline line */}
              <div style={{
                position: 'absolute',
                left: '4.25rem',
                top: '0',
                height: '100%',
                width: '2px',
                backgroundColor: '#e6dbd1',
                transform: 'translateX(-50%)'
              }}></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                {articles.map((article) => (
                <div 
                  key={article.id}
                  id={`event-${article.id}`}
                  style={{ 
                    position: 'relative', 
                    paddingLeft: '5rem' 
                  }}
                >
                  {/* Timeline dot */}
                  <div style={{
                    position: 'absolute',
                    left: '4.25rem',
                    top: '1rem',
                    transform: 'translateX(-50%)',
                    width: '1.25rem',
                    height: '1.25rem',
                    borderRadius: '0.25rem',
                    backgroundColor: '#2563eb',
                    border: '4px solid #fdfcfb',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(-50%) scale(1.5)';
                    e.currentTarget.style.backgroundColor = '#1d4ed8';
                    e.currentTarget.style.boxShadow = '0 0 0 4px rgba(29, 78, 216, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
                    e.currentTarget.style.backgroundColor = '#2563eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  ></div>
                  
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h2 style={{
                      fontSize: '1.875rem',
                      fontWeight: 'bold',
                      color: '#6b5a4b',
                      transition: 'color 0.3s ease',
                      margin: '0',
                      fontFamily: 'Merriweather, serif'
                    }}>
                      {formatYear(article.year || 0)}
                    </h2>
                  </div>
                  
                  <div style={{ marginTop: '1.5rem' }}>
                    <div 
                      style={{
                        backgroundColor: 'white',
                        padding: '1.5rem',
                        borderRadius: '0.5rem',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        border: '1px solid #f3ede8',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0px 8px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: 'bold',
                          color: '#4a3f35',
                          margin: '0',
                          fontFamily: 'Merriweather, serif'
                        }}>
                          {article.title}
                        </h3>
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#2563eb',
                          backgroundColor: '#eff6ff',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Article
                        </span>
                      </div>
                      <p style={{
                        marginTop: '0.5rem',
                        color: '#6b5a4b',
                        lineHeight: '1.6',
                        margin: '0.5rem 0 1rem 0'
                      }}>
                        {article.excerpt}
                      </p>
                      <Link
                        to={`/article/${article.slug}`}
                        style={{
                          marginTop: '1rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: '#c9731d',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          textDecoration: 'none'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Read Article 
                        <span className="material-symbols-outlined" style={{ marginLeft: '0.25rem', fontSize: '1rem' }}>
                          arrow_forward
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .timeline-item:hover .timeline-dot {
          transform: scale(1.5);
          background-color: #c9731d;
          box-shadow: 0 0 0 4px rgba(201, 115, 29, 0.2);
        }
        .timeline-item:hover .timeline-year {
          color: #c9731d;
        }
        .timeline-article-item:hover {
          transform: translateY(-2px);
          box-shadow: 0px 8px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04);
        }
        .quick-timeline-item:hover .quick-timeline-dot {
          transform: scale(1.5);
          background-color: #c9731d;
        }
        .quick-timeline-item:hover .quick-timeline-year {
          color: #c9731d;
          font-weight: 700;
        }
        .quick-timeline-item.active .quick-timeline-dot {
          background-color: #c9731d;
          transform: scale(1.5);
        }
        .quick-timeline-item.active .quick-timeline-year {
          color: #c9731d;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default Timeline;